import { useState } from 'react';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useDepartments, useSaveDepartment, errMessage, type Department } from './systemMasters';
import { DepartmentEmployeesPanel } from './DepartmentEmployeesPanel';

/**
 * The full Department detail form as a stacked dialog (Department + Parent + Sort,
 * with the Employees tab) — opened from a grid/picker edit pencil, mirroring the
 * LIMS "{name} - Department" form. (Child Department tab removed per request.)
 */
export function DepartmentDetailDialog({ department, onClose }: { department: Department; onClose: () => void }) {
  const notify = useNotify();
  const { alert } = useConfirm();
  const save = useSaveDepartment();
  const { data: all = [] } = useDepartments();

  const [name, setName] = useState(department.name ?? '');
  const [parentId, setParentId] = useState(department.parentId ?? '');
  const [sort, setSort] = useState(department.sort != null ? String(department.sort) : '');
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!name.trim()) { setErr('Department must not be empty'); await alert({ title: 'Validation', message: 'Department must not be empty', tone: 'error' }); return; }
    setErr('');
    try {
      await save.mutateAsync({ id: department.id, name: name.trim(), sort: sort.trim() === '' ? null : Number(sort), parentId: parentId || null });
      await alert({ title: 'Success', message: 'Saved successfully.', tone: 'success' });
      onClose();
    } catch (e) { notify.error(errMessage(e)); }
  };

  return (
    <div className="acc-overlay" style={{ zIndex: 1250 }} onMouseDown={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="acc-dialog acc-dialog-lg acc-dialog-emp" onMouseDown={(e) => e.stopPropagation()}>
        <div className="acc-dialog-hdr"><span>{`${name || 'Department'} - Department`}</span><i className="ti ti-x acc-dialog-x" onClick={onClose} /></div>
        <div className="acc-dialog-body">
          <div className="acc-tab-pane acc-compact">
            <div className="acc-field-h">
              <label className="acc-label acc-label-req">Department<span className="req">*</span></label>
              <div className="acc-fh-control">
                <input className="acc-input" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (err) setErr(''); }} onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                {err && <div className="acc-err">{err}</div>}
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">Parent</label>
              <div className="acc-fh-control">
                <div className="acc-lookup">
                  <select className="acc-input" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                    <option value="">N/A</option>
                    {all.filter((d) => d.id !== department.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                  <button type="button" className="acc-lookup-btn" title="Erase" disabled={!parentId} onClick={() => setParentId('')}><i className="ti ti-eraser" /></button>
                </div>
              </div>
            </div>
            <div className="acc-field-h">
              <label className="acc-label">Sort</label>
              <div className="acc-fh-control"><input className="acc-input" type="number" value={sort} placeholder="(auto)" onChange={(e) => setSort(e.target.value)} /></div>
            </div>
          </div>
          <div className="acc-tabs">
            <button type="button" className="acc-tab active"><i className="ti ti-users" /> Employees</button>
          </div>
          <DepartmentEmployeesPanel departmentId={department.id} />
        </div>
        <div className="acc-dialog-ftr">
          <button className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={save.isPending}><span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'OK'}</button>
          <button className="ca-btn" onClick={onClose}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        </div>
      </div>
    </div>
  );
}
